import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { SearchUsecase } from "@/usecases/search/SearchUsecase";
import { usePrivatePageContext } from "@/utils/hooks/private-page.hooks";
import { zodResolver } from "@hookform/resolvers/zod";
import { SearchFormModel, searchFormModel, toFormLanguageSelectBox } from "./search-form-model";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLoading } from "@/utils/hooks/loading.hooks";
import { i18nError, UnauthorizedError } from "@/@types/api-error";
import { DataSchema } from "common";
import { useDebounce } from "@/utils/hooks/debounce.hooks";
import { useAnalytics } from "@/utils/hooks/analytics.hooks";
import { generateRandomKey } from "@/utils/generate-ramdom-key";
import { useNavigate } from "react-router-dom";
import { ChatViewState } from "./ChatDialog/ChatDialog.hooks";
import { useScrollAnchor } from "@/utils/hooks/scroll-anchor.hooks";
import { useToast } from "@/components/ui/use-toast";
import { ToastAction } from "@/components/ui/toast";
import { bookmarksAtom } from "@/atoms/bookmark";
import { documentHistoryAtom } from "@/atoms/documentHistory";
import { DocumentHistoryUsecase } from "@/usecases/document-history/DocumentHistoryUsecase";
import { useAtom, useAtomValue, useSetAtom } from "jotai";
import { BookmarkUsecase } from "@/usecases/bookmark/BookmarkUsecase";
import { DocumentUsecase } from "@/usecases/document/DocumentUsecase";
import { formLanguageSelectBoxAtoms } from "@/atoms/user-settings";
import { Language } from "@/config";
import { ConvertCommand, convertCommandOptions, sortOptions } from "@/@types/search";
import { suggestionsAtom } from "@/atoms/suggestion";
import { removeStartAndEndSpecialCharacter, removeStartSpecialCharacter, searchSuggestion } from "@/utils/suggestion";
import { Model } from "@/atoms/model";
import { searchFormFiltersAtoms } from "@/atoms/form";

const SEARCH_DEBOUNCE_MS = 1000;
export const FILTER_MODEL_GROUP_NONE = "-";
export const FILTER_LANGUAGE_ALL = "*";

export type TotalDocumentGoodState = {
  id: string;
  total: number;
};

export const useSearchPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const { apiClient, logout, user } = usePrivatePageContext();
  const [bookmarks, setBookmarks] = useAtom(bookmarksAtom);
  const setDocumentHistory = useSetAtom(documentHistoryAtom);
  const { loading, showLoading, hideLoading } = useLoading();
  const [apiErrorMessage, setApiErrorMessage] = useState<string>("");
  const [apiErrorDialogOpen, setApiErrorDialogOpen] = useState<boolean>(false);
  const [result, setResult] = useState<DataSchema.SearchResponse>();
  const [currentTab, setCurrentTab] = useState<string | "all">("all");
  const [openChatDialog, setOpenChatDialog] = useState<boolean>(false);
  const [keyphraseComboOpen, setKeyphraseComboOpen] = useState(false);
  const totalSuggestions = useAtomValue(suggestionsAtom);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const { debounce } = useDebounce();
  const { debounce: searchDebounce, clearDebounce: clearSearchDebounce } = useDebounce();
  const { sendSearchEvent, sendSearchSuggestEvent } = useAnalytics();
  const [filterOpen, setFilterOpen] = useState<boolean>(false);
  const filterTriggerRef = useRef<HTMLButtonElement>(null);
  const searchParams = new URLSearchParams(location.search);
  const paramChatId = searchParams.get("chatId");
  const [chatViewState, setChatViewState] = useState<ChatViewState>({ chatId: "", viewType: "close" });
  const { scrollAnchorRef, scrollToAnchor } = useScrollAnchor();
  const { toast } = useToast();
  const defaultLanguage = useAtomValue(formLanguageSelectBoxAtoms);
  const [autoGoodDocumentIdsSet, setAutoGoodDocumentIdsSet] = useState<Set<string>>(new Set<string>());
  const [totalDocumentsGood, setTotalDocumentsGood] = useState<TotalDocumentGoodState[]>([]);
  const [goodDocumentIdsSet, setGoodDocumentIdsSet] = useState<Set<string>>(new Set<string>());
  const [searchKeyphraseHelpOpen, setSearchKeyphraseHelpOpen] = useState<boolean>(false);
  const [initialSearched, setInitialSearched] = useState<boolean>(false);
  const [searchModelOpen, setSearchModelOpen] = useState<boolean>(false);
  const { filterModelGroups, filterLanguages, filterCategories } = useAtomValue(searchFormFiltersAtoms);
  const [hasTabScroll, setHasTabScroll] = useState<boolean>(false);
  const parentScrollAnchorRef = useRef<HTMLDivElement>(null);
  const {
    scrollAnchorRef: tabScrollAnchorRef,
    scrollToPrev: tabScrollToPrev,
    scrollToNext: tabScrollToNext,
  } = useScrollAnchor();

  const onResizeWindow = useCallback(() => {
    const parentWidth = parentScrollAnchorRef?.current?.clientWidth ?? 0;
    const scrollWidth = tabScrollAnchorRef?.current?.clientWidth ?? 0;
    const hasScroll = scrollWidth - parentWidth > 0;
    setHasTabScroll(hasScroll);
  }, [tabScrollAnchorRef]);

  const onClickFilterTrigger = useCallback(() => {
    filterTriggerRef.current?.click();
  }, [filterTriggerRef]);

  const form = useForm<SearchFormModel>({
    resolver: zodResolver(searchFormModel),
    defaultValues: {
      filter: {
        modelGroup: FILTER_MODEL_GROUP_NONE,
        language: defaultLanguage,
        categories: [],
        onlyLatestVersion: true,
      },
      sort: "relevance",
    },
    mode: "onChange",
  });

  const callAutocomplete = useCallback(
    (keyphrase: string) => {
      if (keyphrase.length === 0) {
        setSuggestions([]);
        return;
      }
      sendSearchSuggestEvent();

      const suggestKeyphrase = removeStartSpecialCharacter(keyphrase);
      const updatedSuggestions = searchSuggestion(suggestKeyphrase, totalSuggestions);
      setSuggestions(updatedSuggestions);
    },
    [sendSearchSuggestEvent, totalSuggestions],
  );

  const getSearchDocumentsGood = useCallback(
    async (documentIds: string[]) => {
      SearchUsecase.searchDocumentsGood(apiClient, { ids: documentIds })
        .then((response) => {
          const totalDocumentsGood = response.items.map((item) => {
            return { id: item.id, total: item.total };
          });
          const goodDocumentIdsSet = new Set<string>(
            response.items.filter((item) => item.value === 1).map((item) => item.id),
          );
          setGoodDocumentIdsSet(goodDocumentIdsSet);
          setTotalDocumentsGood(totalDocumentsGood);
        })
        .catch((error) => {
          if (error instanceof UnauthorizedError) {
            logout();
            return;
          }
        });
    },
    [apiClient, logout],
  );

  const executeSearch = form.handleSubmit(
    (data) => {
      setFilterOpen(false);
      setSearchKeyphraseHelpOpen(false);
      showLoading();
      sendSearchEvent();

      SearchUsecase.search(apiClient, data)
        .then((response) => {
          // NOTE: 関連キーワードに検索Suggestionの結果のTop10を含む。
          const submitKeyphrase = removeStartAndEndSpecialCharacter(data.keyphrase ?? "");
          const searchSuggestionsTop10 = searchSuggestion(submitKeyphrase, totalSuggestions).slice(0, 10);
          setResult({
            ...response,
            querySuggestions: searchSuggestionsTop10,
          });
          setCurrentTab("all");
          setAutoGoodDocumentIdsSet(new Set<string>());
          getSearchDocumentsGood(response.items.map((item) => item.documentId));
          debounce(() => {
            onResizeWindow();
          }, 300);
        })
        .catch((error) => {
          if (error instanceof UnauthorizedError) {
            logout();
            return;
          }
          if (error instanceof i18nError) {
            setApiErrorMessage(t(error.i18nKey));
          } else {
            setApiErrorMessage(t("api_error_panic"));
          }
          setApiErrorDialogOpen(true);
        })
        .finally(() => {
          hideLoading();
        });
    },
    (errors) => {
      if (errors.keyphrase) {
        const errorMessage = t("search_keywords_error");
        toast({
          title: errorMessage,
          action: <ToastAction altText={errorMessage}>{t("ok")}</ToastAction>,
        });
      }
    },
  );

  const onSubmit = useCallback(() => {
    clearSearchDebounce();
    executeSearch();
  }, [clearSearchDebounce, executeSearch]);

  const onChangeKeyphrase = useCallback(
    (keyphrase: string) => {
      debounce(() => {
        setSearchKeyphraseHelpOpen(false);
        callAutocomplete(keyphrase);
      });

      form.setValue("keyphrase", keyphrase);
    },
    [callAutocomplete, debounce, form],
  );

  const onClearKeyphrase = useCallback(async () => {
    onChangeKeyphrase("");
  }, [onChangeKeyphrase]);

  const onClickFilterClear = useCallback(() => {
    form.reset({
      keyphrase: form.getValues("keyphrase"),
      filter: {
        modelGroup: FILTER_MODEL_GROUP_NONE,
        modelCode: "",
        modelName: "",
        language: form.getValues("filter.language"),
        categories: [],
      },
      sort: "relevance",
    });
    searchDebounce(() => executeSearch(), SEARCH_DEBOUNCE_MS);
  }, [form, searchDebounce, executeSearch]);

  const sortOptionItems = useMemo(() => {
    return sortOptions.map((option) => {
      return { ...option, label: t(option.i18n) };
    });
  }, [t]);

  const tabCategories = useMemo(() => {
    const categoriesCount: Record<string, number> = {};
    result?.items?.forEach((item) => {
      item?.categories?.forEach((category) => {
        categoriesCount[category] = (categoriesCount[category] ?? 0) + 1;
      });
    });

    return [
      {
        label: t("filter_language_all"),
        value: "all",
        count: result?.items?.length ?? 0,
      },
      ...filterCategories
        .filter((value) => (categoriesCount[value.value] ?? 0) > 0)
        .map((value) => {
          return {
            ...value,
            count: categoriesCount[value.value],
          };
        }),
    ];
  }, [filterCategories, result?.items, t]);

  const filteredSearchResults = useMemo(() => {
    const items = result?.items ?? [];
    if (currentTab === "all") {
      return items;
    }
    return items.filter((item) => item.categories?.includes(currentTab));
  }, [currentTab, result?.items]);

  const updateOpenChatDialog = useCallback(
    (open: boolean) => {
      if (!open) {
        navigate("/");
      }
      setOpenChatDialog(open);
    },
    [navigate],
  );

  const onClickShowChatDialog = useCallback(() => {
    navigate("/?chatId=new");
  }, [navigate]);

  const updateFilterOpen = useCallback(
    (value: string) => {
      const open = value === "filter";
      if (open) {
        scrollToAnchor();
      }
      setFilterOpen(open);
    },
    [scrollToAnchor],
  );

  const bookmarksSet = useMemo(() => {
    const resultSet = new Set<string>();
    bookmarks.forEach((bookmark) => {
      resultSet.add(`${bookmark.pageNo}:${bookmark.documentId}`);
    });
    return resultSet;
  }, [bookmarks]);

  const onClickBookmark = useCallback(
    (data: DataSchema.SearchResultItem, bookmarkOn: boolean) => {
      const documentId = data.documentId;
      const documentTitle = data.documentTitle;
      const pageNo = Number(data.excerptPageNo);

      if (bookmarkOn) {
        BookmarkUsecase.deleteBookmark(apiClient, documentId, pageNo);
        const updatedBookmarks = bookmarks.filter((bookmark) => {
          return !(bookmark.documentId === documentId && bookmark.pageNo === pageNo);
        });
        setBookmarks(updatedBookmarks);
      } else {
        const newBookmark = { documentId, documentTitle, pageNo };
        BookmarkUsecase.addBookmark(apiClient, newBookmark);
        const updatedBookmarks = [newBookmark, ...bookmarks];
        setBookmarks(updatedBookmarks);
      }
    },
    [apiClient, bookmarks, setBookmarks],
  );

  const onChangeLanguage = useCallback(
    (language: Language) => {
      form.setValue("filter.language", toFormLanguageSelectBox(language));
    },
    [form],
  );

  const onSelectSort = useCallback(
    (sort: string) => {
      form.setValue("sort", sort as never);
      onSubmit();
    },
    [form, onSubmit],
  );

  const onClickDocumentTitle = useCallback(
    (data: DataSchema.SearchResultItem) => {
      // Record browsing history (before good-feedback guard to ensure dedup works on re-click)
      setDocumentHistory((prev) =>
        DocumentHistoryUsecase.add(user.userId, prev, {
          documentId: data.documentId,
          documentTitle: data.documentTitle,
          pageNo: Number(data.excerptPageNo),
        }),
      );

      if (autoGoodDocumentIdsSet.has(data.documentId)) return;
      setAutoGoodDocumentIdsSet(new Set([...autoGoodDocumentIdsSet, data.documentId]));

      SearchUsecase.goodFeedback(apiClient, {
        queryId: result!.queryId,
        documentId: data.documentId,
        searchDocumentId: data.searchDocumentId,
      }).catch((error) => {
        const updatedAutoGoodDocumentIdsSet = new Set<string>(autoGoodDocumentIdsSet);
        updatedAutoGoodDocumentIdsSet.delete(data.documentId);
        setAutoGoodDocumentIdsSet(updatedAutoGoodDocumentIdsSet);
        if (error instanceof UnauthorizedError) {
          logout();
          return;
        }
      });
    },
    [apiClient, autoGoodDocumentIdsSet, logout, result, setDocumentHistory, user.userId],
  );

  const onClickGood = useCallback(
    (documentId: string) => {
      const goodOn = goodDocumentIdsSet.has(documentId);
      if (goodOn) {
        DocumentUsecase.deleteDocumentGood(apiClient, documentId);
        const updatedIsGoodDocumentIdsSet = new Set(goodDocumentIdsSet);
        updatedIsGoodDocumentIdsSet.delete(documentId);
        setGoodDocumentIdsSet(updatedIsGoodDocumentIdsSet);

        setTotalDocumentsGood((prevState) => {
          return prevState.map((totalDocumentGood) =>
            totalDocumentGood.id === documentId
              ? { ...totalDocumentGood, total: totalDocumentGood.total - 1 }
              : totalDocumentGood,
          );
        });
      } else {
        DocumentUsecase.addDocumentGood(apiClient, documentId);
        setGoodDocumentIdsSet(new Set([...goodDocumentIdsSet, documentId]));

        setTotalDocumentsGood((prevState) => {
          return prevState.map((totalDocumentGood) =>
            totalDocumentGood.id === documentId
              ? { ...totalDocumentGood, total: totalDocumentGood.total + 1 }
              : totalDocumentGood,
          );
        });
      }
    },
    [apiClient, goodDocumentIdsSet],
  );

  const onClickOpenSearchKeyphraseHelp = useCallback(() => {
    setSearchKeyphraseHelpOpen(true);
  }, []);

  const onClickCloseSearchKeyphraseHelp = useCallback(() => {
    setSearchKeyphraseHelpOpen(false);
  }, []);

  const onClickOpenSearchModel = useCallback(() => {
    setSearchModelOpen(true);
  }, []);

  const onClickCloseSearchModel = useCallback(() => {
    setSearchModelOpen(false);
  }, []);

  const onSelectModel = useCallback(
    (model: Model) => {
      form.setValue("filter.modelCode", model.code);
      form.setValue("filter.modelName", model.name);
      setSearchModelOpen(false);
      searchDebounce(() => executeSearch(), SEARCH_DEBOUNCE_MS);
    },
    [form, searchDebounce, executeSearch],
  );

  const convertCommandFormOptions = useMemo(() => {
    return convertCommandOptions.map((option) => {
      return {
        ...option,
        label: t(option.labelI18n),
      };
    });
  }, [t]);

  const onSelectConvertCommand = useCallback(
    (command: ConvertCommand) => {
      const keyphraseWords = (form.getValues("keyphrase") ?? "")
        .trim()
        .split("　")
        .join(" ")
        .split(" ")
        .map((word) => {
          return word.trim();
        });
      if (keyphraseWords.length === 0) return;
      switch (command) {
        case "extract_march": {
          const w = keyphraseWords.map((word) => `"${word}"`).join(" ");
          form.setValue("keyphrase", w);
          return;
        }
        case "and": {
          const w = keyphraseWords.join(" AND ").trim();
          form.setValue("keyphrase", w);
          return;
        }
        case "or": {
          const w = keyphraseWords.join(" OR ").trim();
          form.setValue("keyphrase", w);
          return;
        }
      }
    },
    [form],
  );

  useEffect(() => {
    if (paramChatId === "new") {
      setChatViewState({ chatId: generateRandomKey(), viewType: "new" });
      setOpenChatDialog(true);
    } else if (paramChatId != null) {
      setChatViewState({ chatId: paramChatId, viewType: "load" });
      setOpenChatDialog(true);
    } else {
      setChatViewState({ chatId: "", viewType: "close" });
      setOpenChatDialog(false);
    }
  }, [paramChatId, updateOpenChatDialog]);

  useEffect(() => {
    const subscription = form.watch((_values, { type, name }) => {
      if (type !== "change" || !name) return;
      if (!name.startsWith("filter.")) return;
      searchDebounce(() => executeSearch(), SEARCH_DEBOUNCE_MS);
    });
    return () => subscription.unsubscribe();
  }, [form, searchDebounce, executeSearch]);

  useEffect(() => {
    // NOTE: ユーザー情報の言語値を反映した場合FormのDefault値が更新されないため、以下の指定で回避する。
    form.setValue("filter.language", defaultLanguage);
  }, [defaultLanguage, form]);

  useEffect(() => {
    window.addEventListener("resize", onResizeWindow);
    return () => window.removeEventListener("resize", onResizeWindow);
  }, [onResizeWindow]);

  // GeSの検索窓から遷移したときに、着地時の検索を実行する
  useEffect(() => {
    // NOTE: executeSearchは毎レンダリング作り直されるため、このEffectも毎レンダリング再実行される。
    // initialSearchedで2回目以降を弾き、検索 → setResult → 再レンダリング → 検索 のループを防ぐ。
    // StrictModeでは開発時のみ2回実行されるが、検索は冪等なので許容している(本番ビルドでは1回)。
    if (initialSearched) return;
    const initialKeyphrase = user.initialSearchKeyphrase;
    if (!initialKeyphrase) return;

    setInitialSearched(true);
    // NOTE: 検索実行後もユーザーが絞り込み直せるように、検索文字はフォームに残す
    form.setValue("keyphrase", initialKeyphrase);
    // NOTE: defaultLanguageはi18nextのlanguageChangedイベント経由で届くが、
    // このイベントはロケールファイルの取得完了後に発火するため、着地直後はブラウザ検出言語のまま。
    // 検出言語は "ja" のような言語コードのみのことがあり、langidは "ja-JP" 形式なので検索が0件になる。
    // 初回検索は仕様どおりユーザーのLanguageIdで実行する。
    form.setValue("filter.language", user.langId);
    clearSearchDebounce();
    executeSearch();
  }, [initialSearched, user.initialSearchKeyphrase, user.langId, form, clearSearchDebounce, executeSearch]);

  return {
    t,
    form,
    loading,
    onSubmit,
    result,
    apiErrorMessage,
    apiErrorDialogOpen,
    setApiErrorDialogOpen,
    onClickShowChatDialog,
    openChatDialog,
    updateOpenChatDialog,
    chatViewState,
    keyphraseComboOpen,
    setKeyphraseComboOpen,
    suggestions,
    onChangeKeyphrase,
    onClearKeyphrase,
    filterModelGroups,
    filterLanguages,
    filterCategories,
    onClickFilterClear,
    currentTab,
    filterOpen,
    updateFilterOpen,
    onClickFilterTrigger,
    filterTriggerRef,
    scrollAnchorRef,
    toast,
    tabCategories,
    setCurrentTab,
    filteredSearchResults,
    onClickBookmark,
    bookmarksSet,
    onClickGood,
    onChangeLanguage,
    sortOptions: sortOptionItems,
    onSelectSort,
    onClickDocumentTitle,
    totalDocumentsGood,
    goodDocumentIdsSet,
    searchKeyphraseHelpOpen,
    onClickOpenSearchKeyphraseHelp,
    onClickCloseSearchKeyphraseHelp,
    searchModelOpen,
    onClickOpenSearchModel,
    onClickCloseSearchModel,
    onSelectModel,
    firLink: user.firLink,
    onSelectConvertCommand,
    convertCommandFormOptions,
    tabScrollAnchorRef,
    tabScrollToPrev,
    tabScrollToNext,
    parentScrollAnchorRef,
    hasTabScroll,
  };
};
